import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  confirmBoxMasterImport,
  previewBoxMasterImport,
} from '../services/industrialCostService';
import type {
  BoxMasterIssue,
  BoxMasterKind,
  BoxMasterPreviewResponse,
} from '../services/industrialCostService';

// IC-STOCK-05: subida de los Excel de cajas. Cada confirmación sustituye
// entera la relación anterior.

const KIND_LABELS: Record<BoxMasterKind, { title: string; columns: [string, string][] }> = {
  'customer-personalizations': {
    title: 'Cajas · personalización por cliente',
    columns: [
      ['customer_number', 'Cliente'],
      ['personalization', 'Personalización'],
    ],
  },
  'box-purchase-mappings': {
    title: 'Cajas · caja real por personalización',
    columns: [
      ['generic_box_type', 'Caja genérica'],
      ['personalization', 'Personalización'],
      ['purchased_material_code', 'Caja real'],
    ],
  },
};

type IndustrialCostBoxMasterSourceDialogProps = {
  open: boolean;
  kind: BoxMasterKind;
  file: File;
  onClose: () => void;
  onImported: (message: string) => void;
};

function getErrorMessage(error: unknown, fallback: string): string {
  if (typeof error === 'object' && error !== null) {
    const maybeResponse = error as {
      response?: { data?: { detail?: unknown } };
    };
    const detail = maybeResponse.response?.data?.detail;

    if (typeof detail === 'string') return detail;

    if (
      typeof detail === 'object' &&
      detail !== null &&
      'message' in detail &&
      typeof (detail as { message: unknown }).message === 'string'
    ) {
      return (detail as { message: string }).message;
    }
  }

  return fallback;
}

function IssueList({
  issues,
  severity,
}: {
  issues: BoxMasterIssue[];
  severity: 'error' | 'warning';
}) {
  if (issues.length === 0) return null;

  return (
    <Stack spacing={1}>
      {issues.map((issue, index) => (
        <Alert key={`${issue.code}-${index}`} severity={severity} variant="outlined">
          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
            {issue.message}
          </Typography>
          {issue.rows.length > 0 ? (
            <Typography sx={{ fontSize: 12, opacity: 0.8 }}>
              Filas del Excel: {issue.rows.slice(0, 20).join(', ')}
              {issue.rows.length > 20 ? '…' : ''}
            </Typography>
          ) : null}
        </Alert>
      ))}
    </Stack>
  );
}

export default function IndustrialCostBoxMasterSourceDialog({
  open,
  kind,
  file,
  onClose,
  onImported,
}: IndustrialCostBoxMasterSourceDialogProps) {
  const [preview, setPreview] = useState<BoxMasterPreviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const labels = KIND_LABELS[kind];

  useEffect(() => {
    let cancelled = false;

    async function loadPreview() {
      setLoading(true);
      setErrorMessage(null);
      setPreview(null);

      try {
        const result = await previewBoxMasterImport(kind, file);
        if (!cancelled) setPreview(result);
      } catch (error) {
        if (!cancelled) {
          setErrorMessage(
            getErrorMessage(error, 'No se pudo analizar el fichero.'),
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    void loadPreview();

    return () => {
      cancelled = true;
    };
  }, [kind, file]);

  async function handleConfirm() {
    setConfirming(true);
    setErrorMessage(null);

    try {
      const result = await confirmBoxMasterImport(kind, file);
      onImported(
        `${result.imported_count} filas cargadas desde ${result.source_file_name}.`,
      );
    } catch (error) {
      setErrorMessage(
        getErrorMessage(error, 'No se pudo cargar el fichero.'),
      );

      const rejectedPreview = (
        error as {
          response?: { data?: { detail?: { preview?: BoxMasterPreviewResponse } } };
        }
      ).response?.data?.detail?.preview;

      if (rejectedPreview) {
        setPreview({ ...rejectedPreview, source_file_name: file.name });
      }
    } finally {
      setConfirming(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={confirming ? undefined : onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>{labels.title}</DialogTitle>

      <DialogContent dividers>
        <Stack spacing={2}>
          <Alert severity="info" variant="outlined">
            El Excel es el maestro: al confirmar, sustituye entera la relación
            anterior. Si algo no cuadra, corrígelo en el Excel y vuelve a
            subirlo.
          </Alert>

          <Typography sx={{ fontSize: 13 }}>
            Fichero: <strong>{file.name}</strong>
          </Typography>

          {loading ? (
            <Alert severity="info">Analizando el fichero…</Alert>
          ) : null}

          {errorMessage ? (
            <Alert severity="error">{errorMessage}</Alert>
          ) : null}

          {preview ? (
            <>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip label={`Filas leídas: ${preview.source_rows_count}`} />
                <Chip
                  color="success"
                  label={`Filas a cargar: ${preview.valid_rows_count}`}
                />
                {preview.duplicate_rows_count > 0 ? (
                  <Chip label={`Repetidas: ${preview.duplicate_rows_count}`} />
                ) : null}
                {preview.blocking_errors.length > 0 ? (
                  <Chip
                    color="error"
                    label={`Errores: ${preview.blocking_errors.length}`}
                  />
                ) : null}
                {preview.warnings.length > 0 ? (
                  <Chip
                    color="warning"
                    label={`Avisos: ${preview.warnings.length}`}
                  />
                ) : null}
              </Stack>

              <IssueList issues={preview.blocking_errors} severity="error" />
              <IssueList issues={preview.warnings} severity="warning" />

              {preview.sample_rows.length > 0 ? (
                <Box sx={{ overflowX: 'auto' }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, mb: 0.5 }}>
                    Muestra de filas a cargar
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        {labels.columns.map(([key, label]) => (
                          <TableCell key={key}>{label}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {preview.sample_rows.map((row, index) => (
                        <TableRow key={index}>
                          {labels.columns.map(([key]) => (
                            <TableCell key={key}>{row[key] ?? '—'}</TableCell>
                          ))}
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              ) : null}
            </>
          ) : null}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={confirming}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={() => void handleConfirm()}
          disabled={!preview?.can_confirm || loading || confirming}
        >
          {confirming ? 'Cargando…' : 'Sustituir con este fichero'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
